import React, { useState, useEffect, useMemo } from 'react';
import {
  Building2, Users, Monitor, Shield, Plus, CheckCircle2,
  AlertCircle, ChevronRight, Fingerprint, Eye, Trash2, Key,
  Mail, Phone, MapPin, X, Check, Laptop, Radio, Sparkles, RefreshCw
} from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';
import Navbar from './Navbar';

export default function SaasClientsManagement() {
  const [clients, setClients] = useState([]);
  const [unassignedDevices, setUnassignedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Onboard modal state
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardSubmitting, setOnboardSubmitting] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    organization_name: '',
    legal_name: '',
    contact_email: '',
    password: '',
    admin_first_name: '',
    admin_last_name: '',
    mobile_number: '',
    address: '',
    plan_tier: 'T700_STANDARD',
    staff_limit: 50,
    assigned_dev_ids: []
  });

  // Assign device modal
  const [assignModal, setAssignModal] = useState({
    open: false,
    org: null,
    selectedDevId: '',
    saving: false
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [clientsRes, devsRes] = await Promise.all([
        axiosInstance.get('/api/organization/saas/clients'),
        axiosInstance.get('/api/organization/saas/unassigned-devices')
      ]);

      if (clientsRes.data?.status) {
        setClients(clientsRes.data.data || []);
      }
      if (devsRes.data?.status) {
        setUnassignedDevices(devsRes.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load SaaS clients:', err);
      setError(err.response?.data?.message || 'Failed to load client companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalStaffEnrolled = useMemo(() => {
    return clients.reduce((acc, c) => acc + (Number(c.staff_count) || 0), 0);
  }, [clients]);

  const totalMachinesBound = useMemo(() => {
    return clients.reduce((acc, c) => acc + (c.devices?.length || 0), 0);
  }, [clients]);

  const handleOnboardSubmit = async (e) => {
    e.preventDefault();
    if (!onboardForm.organization_name || !onboardForm.contact_email || !onboardForm.password || !onboardForm.admin_first_name) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setOnboardSubmitting(true);
      const res = await axiosInstance.post('/api/organization/saas/onboard', onboardForm);
      if (res.data?.status) {
        setShowOnboardModal(false);
        setOnboardForm({
          organization_name: '',
          legal_name: '',
          contact_email: '',
          password: '',
          admin_first_name: '',
          admin_last_name: '',
          mobile_number: '',
          address: '',
          plan_tier: 'T700_STANDARD',
          staff_limit: 50,
          assigned_dev_ids: []
        });
        fetchData();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Onboarding failed');
    } finally {
      setOnboardSubmitting(false);
    }
  };

  const handleAssignDeviceSubmit = async () => {
    if (!assignModal.selectedDevId || !assignModal.org) return;
    try {
      setAssignModal((prev) => ({ ...prev, saving: true }));
      await axiosInstance.post('/api/organization/saas/assign-device', {
        dev_id: assignModal.selectedDevId,
        organization_id_PK: assignModal.org.organization_id_PK
      });
      setAssignModal({ open: false, org: null, selectedDevId: '', saving: false });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign device');
      setAssignModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleUnassignDevice = async (devId) => {
    if (!window.confirm(`Unassign machine ${devId} from this company?`)) return;
    try {
      await axiosInstance.post('/api/organization/saas/assign-device', {
        dev_id: devId,
        organization_id_PK: null
      });
      fetchData();
    } catch (err) {
      alert('Failed to unassign device');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-16">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                Client Companies & Licensing (SaaS Admin)
              </h1>
            </div>
            <p className="text-blue-100 text-sm sm:text-base max-w-2xl">
              Onboard new organizations, assign T700 / T800 biometric hardware, and manage subscription limits.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowOnboardModal(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold text-white shadow-lg shadow-blue-500/30 transition active:scale-95"
            >
              <Plus className="w-5 h-5" />
              Onboard New Company
            </button>
            <button
              onClick={fetchData}
              className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition"
              title="Refresh Clients"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Active Companies</p>
              <p className="text-2xl font-bold text-slate-900">{clients.length}</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <Monitor className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Assigned Machines</p>
              <p className="text-2xl font-bold text-emerald-600">{totalMachinesBound}</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total Enrolled Staff</p>
              <p className="text-2xl font-bold text-indigo-600">{totalStaffEnrolled}</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
              <Laptop className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Unassigned Machines</p>
              <p className="text-2xl font-bold text-amber-600">{unassignedDevices.length}</p>
            </div>
          </div>
        </div>

        {/* Clients List */}
        <div className="space-y-6">
          <h2 className="text-lg font-bold text-slate-800">Client Organizations</h2>

          {loading && clients.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center border border-slate-200">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-slate-500 font-medium">Loading client companies...</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center border border-slate-200">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-slate-800 mb-1">No Client Companies Found</h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto mb-4">
                Click the button below to onboard your first client company and assign a T700 or T800 machine.
              </p>
              <button
                onClick={() => setShowOnboardModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white font-semibold text-sm shadow-md"
              >
                <Plus className="w-4 h-4" />
                Onboard Company
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {clients.map((org) => {
                const isT800 = org.plan_tier === 'T800_ENTERPRISE';
                const staffUsagePercent = Math.min(
                  100,
                  Math.round(((org.staff_count || 0) / (org.staff_limit || 50)) * 100)
                );

                return (
                  <div
                    key={org.organization_id_PK}
                    className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-6 hover:shadow-md transition"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-slate-100">
                      {/* Company Info */}
                      <div className="flex items-start sm:items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 border border-slate-200 flex items-center justify-center font-bold text-xl text-blue-700 overflow-hidden flex-shrink-0">
                          {org.organization_logo ? (
                            <img src={org.organization_logo} alt="" className="w-full h-full object-contain" />
                          ) : (
                            org.organization_name[0]
                          )}
                        </div>

                        <div>
                          <div className="flex flex-wrap items-center gap-2.5 mb-1">
                            <h3 className="text-xl font-bold text-slate-900">
                              {org.organization_name}
                            </h3>
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                                isT800
                                  ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                  : 'bg-blue-100 text-blue-800 border border-blue-200'
                              }`}
                            >
                              {isT800 ? 'T800 Enterprise (Face/Palm)' : 'T700 Standard (RFID/Finger)'}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                              Active
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 font-medium">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5 text-slate-400" />
                              {org.contact_email}
                            </span>
                            {org.address && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5 text-slate-400" />
                                {org.address}
                              </span>
                            )}
                            <span className="text-slate-400">
                              Org ID: #{org.organization_id_PK}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Staff Limit Progress */}
                      <div className="min-w-[200px]">
                        <div className="flex items-center justify-between text-xs font-semibold mb-1">
                          <span className="text-slate-500">Staff Limit</span>
                          <span className="text-slate-900">
                            {org.staff_count || 0} / {org.staff_limit || 50} Staff
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              staffUsagePercent > 85 ? 'bg-amber-500' : 'bg-blue-600'
                            }`}
                            style={{ width: `${staffUsagePercent}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Assigned Hardware Section */}
                    <div className="pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5 flex items-center gap-1.5">
                          <Monitor className="w-3.5 h-3.5 text-blue-600" />
                          Assigned Biometric Machines ({org.devices?.length || 0})
                        </p>

                        {org.devices && org.devices.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            {org.devices.map((dev) => (
                              <div
                                key={dev.dev_id}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700"
                              >
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="font-semibold text-slate-900">
                                  {dev.name || 'Biometric Unit'}
                                </span>
                                <span className="text-slate-400 font-mono">({dev.dev_id})</span>
                                <button
                                  onClick={() => handleUnassignDevice(dev.dev_id)}
                                  className="text-slate-400 hover:text-rose-600 transition ml-1"
                                  title="Unassign device"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 italic">
                            No biometric machines bound yet.
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setAssignModal({ open: true, org, selectedDevId: '', saving: false })}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Bind Machine
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ONBOARD NEW COMPANY MODAL */}
      {showOnboardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-blue-700 to-indigo-700 p-6 text-white flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">Onboard New Client Company</h3>
                <p className="text-xs text-blue-100 mt-0.5">
                  Set up company profile, admin login credentials, and machine licensing.
                </p>
              </div>
              <button
                onClick={() => setShowOnboardModal(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleOnboardSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Company Details */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  Company Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Apex Logistics"
                      value={onboardForm.organization_name}
                      onChange={(e) => setOnboardForm({ ...onboardForm, organization_name: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Legal Entity Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Apex Logistics Pvt Ltd"
                      value={onboardForm.legal_name}
                      onChange={(e) => setOnboardForm({ ...onboardForm, legal_name: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Office Address
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Vijay Nagar, Indore, Madhya Pradesh"
                      value={onboardForm.address}
                      onChange={(e) => setOnboardForm({ ...onboardForm, address: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>
              </div>

              {/* Admin Account Credentials */}
              <div className="pt-2 border-t border-slate-100">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  Admin Login Account
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Admin First Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rajesh"
                      value={onboardForm.admin_first_name}
                      onChange={(e) => setOnboardForm({ ...onboardForm, admin_first_name: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Admin Last Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Sharma"
                      value={onboardForm.admin_last_name}
                      onChange={(e) => setOnboardForm({ ...onboardForm, admin_last_name: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Login Email *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="admin@company.com"
                      value={onboardForm.contact_email}
                      onChange={(e) => setOnboardForm({ ...onboardForm, contact_email: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      required
                      placeholder="Password"
                      value={onboardForm.password}
                      onChange={(e) => setOnboardForm({ ...onboardForm, password: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>
              </div>

              {/* Hardware & Subscription Plan */}
              <div className="pt-2 border-t border-slate-100">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  Hardware & Plan Tier
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Subscription Plan
                    </label>
                    <select
                      value={onboardForm.plan_tier}
                      onChange={(e) => setOnboardForm({ ...onboardForm, plan_tier: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="T700_STANDARD">T700 Standard (RFID + Fingerprint)</option>
                      <option value="T800_ENTERPRISE">T800 Enterprise (Face + Palm + RFID + Finger)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Max Staff Limit
                    </label>
                    <input
                      type="number"
                      value={onboardForm.staff_limit}
                      onChange={(e) => setOnboardForm({ ...onboardForm, staff_limit: Number(e.target.value) })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  {unassignedDevices.length > 0 && (
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">
                        Select Available Machine to Bind (Optional)
                      </label>
                      <select
                        onChange={(e) => {
                          const val = e.target.value;
                          setOnboardForm({
                            ...onboardForm,
                            assigned_dev_ids: val ? [val] : []
                          });
                        }}
                        className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="">-- No machine bound initially --</option>
                        {unassignedDevices.map((d) => (
                          <option key={d.dev_id} value={d.dev_id}>
                            {d.name || 'Device'} ({d.dev_id}) - IP: {d.ip || 'Unknown'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowOnboardModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={onboardSubmitting}
                  className="px-5 py-2.5 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-md transition disabled:opacity-50"
                >
                  {onboardSubmitting ? 'Onboarding...' : 'Confirm & Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BIND MACHINE MODAL */}
      {assignModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              Bind Machine to {assignModal.org?.organization_name}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Select an unassigned T700 or T800 machine from inventory.
            </p>

            {unassignedDevices.length === 0 ? (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs mb-4">
                No unassigned machines currently connected. Power on the device and point it to server port 4002.
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Available Machines
                </label>
                <select
                  value={assignModal.selectedDevId}
                  onChange={(e) => setAssignModal({ ...assignModal, selectedDevId: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="">-- Choose Machine --</option>
                  {unassignedDevices.map((d) => (
                    <option key={d.dev_id} value={d.dev_id}>
                      {d.name || 'Unit'} ({d.dev_id}) - {d.ip || 'Ready'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setAssignModal({ open: false, org: null, selectedDevId: '', saving: false })}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                disabled={!assignModal.selectedDevId || assignModal.saving}
                onClick={handleAssignDeviceSubmit}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white transition disabled:opacity-50"
              >
                {assignModal.saving ? 'Binding...' : 'Bind Device'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
